// src/pages/TecnologiaPage.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header'; // IMPORTAMOS EL HEADER
import { 
  ArrowLeftIcon,
  CodeBracketIcon,
  CpuChipIcon,
  CloudIcon,
  ServerIcon,
  DevicePhoneMobileIcon,
  LockClosedIcon,
  CommandLineIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
  CircleStackIcon,
  GlobeAltIcon,
  WrenchScrewdriverIcon,
  BoltIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

// Tecnologías principales
const mainTechnologies = [
  {
    category: "Frontend",
    icon: CodeBracketIcon,
    technologies: [
      { name: "React", description: "Biblioteca para interfaces de usuario interactivas", level: "Experto" },
      { name: "TypeScript", description: "JavaScript con tipado estático para código robusto", level: "Experto" },
      { name: "Next.js", description: "Framework React para producción", level: "Avanzado" },
      { name: "Tailwind CSS", description: "Framework CSS utility-first", level: "Experto" },
      { name: "Framer Motion", description: "Animaciones profesionales para React", level: "Avanzado" },
      { name: "PWA", description: "Progressive Web Apps con funcionamiento offline", level: "Experto" }
    ],
    color: "from-[#4ADE80] to-[#3BC571]"
  },
  {
    category: "Backend",
    icon: ServerIcon,
    technologies: [
      { name: "Node.js", description: "Entorno de ejecución JavaScript", level: "Experto" },
      { name: "Express.js", description: "Framework web rápido y minimalista", level: "Experto" },
      { name: "Python", description: "Lenguaje versátil para backend y data science", level: "Avanzado" },
      { name: "Django", description: "Framework Python de alto nivel", level: "Avanzado" },
      { name: "REST APIs", description: "Arquitectura de APIs robustas", level: "Experto" },
      { name: "GraphQL", description: "Lenguaje de consulta para APIs", level: "Avanzado" }
    ],
    color: "from-[#0E2544] to-[#2D4A7D]"
  },
  {
    category: "Bases de Datos",
    icon: CircleStackIcon,
    technologies: [
      { name: "MongoDB", description: "Base de datos NoSQL escalable", level: "Experto" },
      { name: "PostgreSQL", description: "Base de datos relacional robusta", level: "Avanzado" },
      { name: "MySQL", description: "Base de datos relacional popular", level: "Avanzado" },
      { name: "Redis", description: "Base de datos en memoria para caching", level: "Avanzado" },
      { name: "Firebase", description: "Plataforma de desarrollo backend", level: "Experto" }
    ],
    color: "from-[#4ADE80] to-[#2D4A7D]"
  },
  {
    category: "DevOps & Cloud",
    icon: CloudIcon,
    technologies: [
      { name: "AWS", description: "Amazon Web Services - Cloud computing", level: "Avanzado" },
      { name: "Docker", description: "Contenerización de aplicaciones", level: "Experto" },
      { name: "Kubernetes", description: "Orquestación de contenedores", level: "Avanzado" },
      { name: "CI/CD", description: "Integración y despliegue continuo", level: "Experto" },
      { name: "Vercel", description: "Plataforma de deploy frontend", level: "Experto" },
      { name: "Git", description: "Control de versiones", level: "Experto" }
    ],
    color: "from-[#2D4A7D] to-[#4ADE80]"
  },
  {
    category: "Mobile",
    icon: DevicePhoneMobileIcon,
    technologies: [
      { name: "React Native", description: "Apps móviles nativas con React", level: "Avanzado" },
      { name: "PWA", description: "Progressive Web Apps", level: "Experto" },
      { name: "iOS", description: "Desarrollo para Apple", level: "Avanzado" },
      { name: "Android", description: "Desarrollo para Google", level: "Avanzado" }
    ],
    color: "from-[#3BC571] to-[#4ADE80]"
  },
  {
    category: "Seguridad",
    icon: LockClosedIcon,
    technologies: [
      { name: "JWT", description: "Autenticación basada en tokens", level: "Experto" },
      { name: "OAuth 2.0", description: "Protocolo de autorización", level: "Avanzado" },
      { name: "SSL/TLS", description: "Cifrado de comunicaciones", level: "Experto" },
      { name: "Encriptación AES", description: "Cifrado de datos sensibles", level: "Avanzado" },
      { name: "OWASP", description: "Buenas prácticas de seguridad", level: "Avanzado" }
    ],
    color: "from-[#0E2544] to-[#4ADE80]"
  }
];

// Herramientas adicionales
const tools = [
  { name: "VS Code", icon: CommandLineIcon, description: "Editor de código" },
  { name: "Postman", icon: BoltIcon, description: "Testing de APIs" },
  { name: "Figma", icon: SparklesIcon, description: "Diseño UI/UX" },
  { name: "Jira", icon: WrenchScrewdriverIcon, description: "Gestión de proyectos" },
  { name: "Slack", icon: GlobeAltIcon, description: "Comunicación de equipos" },
  { name: "GitHub", icon: CodeBracketIcon, description: "Control de versiones" }
];

const TecnologiaPage: React.FC = () => {
  const navigate = useNavigate();

  // Función para obtener color según nivel
  const getLevelColor = (level: string) => {
    switch(level) {
      case 'Experto': return 'bg-green-100 text-green-800';
      case 'Avanzado': return 'bg-blue-100 text-blue-800';
      case 'Intermedio': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <SEO
  title="Tecnologías | Algorithmia - Stack Moderno para Desarrollo de Software"
  description="Conocé el stack tecnológico de Algorithmia: React, Node.js, TypeScript, MongoDB, AWS, PWA, Docker y más. Tecnologías modernas para construir software robusto y escalable en Córdoba."
  keywords="stack tecnológico, react, nodejs, typescript, mongodb, aws, pwa, docker, desarrollo software córdoba"
  url="https://algorithmia.com.ar/tecnologia"
  canonicalUrl="https://algorithmia.com.ar/tecnologia"  // ✅ agregado
/>


      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* HEADER REUTILIZABLE */}
        <Header isSplashLoading={false} />

        {/* Hero Section - Ajustado con padding-top */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0E2544] via-[#1c3558] to-[#2D4A7D] overflow-hidden">
          {/* Partículas de fondo */}
          <div className="absolute inset-0 opacity-20">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-[#4ADE80]"
                style={{
                  width: `${Math.random() * 6 + 2}px`,
                  height: `${Math.random() * 6 + 2}px`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, (Math.random() - 0.5) * 50],
                  x: [0, (Math.random() - 0.5) * 50],
                  opacity: [0.2, 0.8, 0.2],
                }}
                transition={{
                  duration: Math.random() * 5 + 5,
                  repeat: Infinity,
                  repeatType: 'reverse',
                }}
              />
            ))}
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
                <CpuChipIcon className="h-4 w-4 text-[#4ADE80]" />
                <span className="text-gray-200 text-sm font-medium">Stack Tecnológico</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Tecnologías que{' '}
                <span className="bg-gradient-to-r from-[#4ADE80] to-[#3BC571] bg-clip-text text-transparent">
                  Potencian
                </span>{' '}
                tus Proyectos
              </h1>
              
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Utilizamos las tecnologías más modernas y robustas para garantizar 
                soluciones escalables, seguras y de alto rendimiento.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Grid de Tecnologías */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            {mainTechnologies.map((category, categoryIndex) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={categoryIndex}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                  viewport={{ once: true }}
                  className="mb-16 last:mb-0"
                >
                  {/* Título de categoría */}
                  <div className="flex items-center gap-4 mb-8">
                    <div className={`p-4 rounded-xl bg-gradient-to-r ${category.color} shadow-lg`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900">{category.category}</h2>
                  </div>

                  {/* Grid de tecnologías */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {category.technologies.map((tech, techIndex) => (
                      <motion.div
                        key={techIndex}
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, delay: techIndex * 0.05 }}
                        viewport={{ once: true }}
                        whileHover={{ 
                          y: -5,
                          boxShadow: '0 20px 30px -10px rgba(74, 222, 128, 0.2)',
                          borderColor: '#4ADE80'
                        }}
                        className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-xl font-bold text-gray-900">{tech.name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getLevelColor(tech.level)}`}>
                            {tech.level}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-4">{tech.description}</p>
                        
                        {/* Barra de nivel visual */}
                        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: tech.level === 'Experto' ? '90%' : '75%' }}
                            transition={{ duration: 1, delay: 0.5 }}
                            className={`h-full rounded-full bg-gradient-to-r ${category.color}`}
                          />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Sección de Herramientas */}
        <section className="py-16 bg-gray-50 border-y border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Herramientas que <span className="text-[#4ADE80]">Potencian</span> nuestro Trabajo
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Además de las tecnologías principales, utilizamos estas herramientas para optimizar nuestro flujo de trabajo.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {tools.map((tool, index) => {
                const Icon = tool.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    whileHover={{ 
                      scale: 1.1,
                      rotate: 5,
                      transition: { duration: 0.2 }
                    }}
                    className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 text-center group"
                  >
                    <div className="w-16 h-16 mx-auto bg-gradient-to-r from-[#4ADE80]/10 to-[#3BC571]/10 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform group-hover:from-[#4ADE80]/20 group-hover:to-[#3BC571]/20">
                      <Icon className="h-8 w-8 text-[#4ADE80]" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1">{tool.name}</h3>
                    <p className="text-xs text-gray-500">{tool.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-r from-[#0E2544] to-[#2D4A7D]">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <RocketLaunchIcon className="h-16 w-16 text-[#4ADE80] mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                ¿Listo para Construir algo Increíble?
              </h2>
              <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
                Combinamos estas tecnologías para crear soluciones a medida que transforman tu negocio.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: '0 10px 25px rgba(74, 222, 128, 0.3)' }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const event = new CustomEvent('openContactModal');
                    window.dispatchEvent(event);
                  }}
                  className="px-8 py-4 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Contactar Ahora
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/proyectos')}
                  className="px-8 py-4 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-xl border-2 border-white/50 hover:bg-white/30 transition-all duration-300"
                >
                  Ver Proyectos
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} Algorithmia. Todos los derechos reservados.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TecnologiaPage;